/**
 * The mixer — one strip per loaded pad, plus the master with its limiter and gain-reduction
 * meter and the master effect rack.
 *
 * Fader moves coalesce into a single undo step per gesture, which is why every slider write
 * carries a `coalesceKey`: a drag is one edit to the user, and an undo stack with two
 * hundred entries for one fader move is an undo stack nobody uses.
 */

import { h, list, onCleanup } from '../../core/dom';
import { computed, effect } from '../../core/signal';
import { icon, iconButton, knob, neonButton, parameterRow, toggle } from '../components/controls';
import { gainReductionMeter, levelMeter, stereoMeter } from '../components/meters';
import { emptyState } from '../components/sheet';
import {
  EFFECT_KINDS,
  effectKindDisplayName,
  effectKindParameters,
  effectKindSymbolName,
  makeEffectNode,
  masterVolumeTarget,
  padIsEmpty,
  padPanTarget,
  padVolumeTarget,
  type EffectNode,
  type Pad,
} from '../../model/types';
import { engine, mutate, project, revision, sequencer } from '../../app/state';
import { navigate } from '../../app/router';
import { presentMenu, requiresProject, screenBody, screenHeader, section } from '../../app/shell';

function editPad(padID: string, label: string, change: (pad: Pad) => void, coalesceKey?: string): void {
  mutate(
    label,
    (open) => {
      const live = open.banks.flatMap((bank) => bank.pads).find((entry) => entry.id === padID);
      if (live !== undefined) change(live);
    },
    coalesceKey === undefined ? {} : { coalesceKey },
  );
}

function padStrip(pad: Pad): HTMLElement {
  const meter = levelMeter({ orientation: 'vertical', segments: 20, title: `${pad.name} output`, class: 'strip-meter' });
  const stop = effect(() => {
    engine.meterTick.value;
    meter.setLevel(engine.padLevel(pad.id));
  });

  const volume = h('input', {
    class: 'fader',
    type: 'range',
    min: '0',
    max: '1',
    step: '0.01',
    value: String(pad.volume),
    'aria-label': `${pad.name} volume`,
    oninput: (event: Event) => {
      const value = Number.parseFloat((event.target as HTMLInputElement).value);
      editPad(pad.id, 'Pad Volume', (live) => (live.volume = value), `volume-${pad.id}`);
      // The producer behind "Record knob moves". Pad volume and pan are the two targets both
      // the live sequencer and the offline bounce replay, so a sweep recorded here survives
      // into the export rather than only into the session.
      sequencer.writeAutomation(padVolumeTarget(pad.id), value);
    },
  }) as HTMLInputElement;

  const pan = h('input', {
    class: 'pan',
    type: 'range',
    min: '-1',
    max: '1',
    step: '0.02',
    value: String(pad.pan),
    'aria-label': `${pad.name} pan`,
    oninput: (event: Event) => {
      const value = Number.parseFloat((event.target as HTMLInputElement).value);
      editPad(pad.id, 'Pad Pan', (live) => (live.pan = value), `pan-${pad.id}`);
      sequencer.writeAutomation(padPanTarget(pad.id), value);
    },
  }) as HTMLInputElement;

  const strip = h(
    'div',
    { class: 'mixer-strip' },
    h('p', { class: 'kicker truncate' }, pad.name === '' ? `Pad ${pad.slot + 1}` : pad.name),
    h('div', { class: 'strip-body' }, meter.el, volume),
    h('div', { class: 'strip-pan' }, h('span', { class: 'kicker' }, 'Pan'), pan),
    h(
      'div',
      { class: 'strip-flags' },
      h(
        'button',
        {
          class: ['strip-flag', pad.isMuted ? 'strip-flag--mute' : ''],
          type: 'button',
          'aria-label': `Mute ${pad.name}`,
          'aria-pressed': pad.isMuted ? 'true' : 'false',
          onclick: () => editPad(pad.id, 'Mute Pad', (live) => (live.isMuted = !live.isMuted)),
        },
        'M',
      ),
      h(
        'button',
        {
          class: ['strip-flag', pad.isSoloed ? 'strip-flag--solo' : ''],
          type: 'button',
          'aria-label': `Solo ${pad.name}`,
          'aria-pressed': pad.isSoloed ? 'true' : 'false',
          onclick: () => editPad(pad.id, 'Solo Pad', (live) => (live.isSoloed = !live.isSoloed)),
        },
        'S',
      ),
    ),
  );
  onCleanup(strip, () => {
    stop();
    meter.destroy();
  });
  return strip;
}

/** The master effect rack. Adding, reordering, bypassing and every parameter, all live. */
function masterRack(): HTMLElement {
  const host = h('div', { class: 'rack' });
  const stop = effect(() => {
    revision.value;
    const open = project.value;
    host.replaceChildren();
    if (open === null) return;
    const chain = open.master.chain;
    if (chain.nodes.length === 0) {
      host.appendChild(
        emptyState({
          symbol: 'slider.horizontal.3',
          title: 'No master effects',
          message: 'Add a compressor to glue the mix, or an EQ to shape the whole board at once.',
        }),
      );
      return;
    }
    chain.nodes.forEach((node, index) => {
      host.appendChild(rackRow(node, index, chain.nodes.length));
    });
  });
  onCleanup(host, stop);
  return host;
}

function editMaster(label: string, change: (chainNodes: EffectNode[]) => void): void {
  mutate(label, (open) => change(open.master.chain.nodes));
}

function rackRow(node: EffectNode, index: number, total: number): HTMLElement {
  const controls = effectKindParameters(node.kind).map(
    (descriptor) =>
      parameterRow({
        descriptor,
        value: node.values[descriptor.key] ?? descriptor.defaultValue,
        enabled: !node.isBypassed,
        onChange: (value) =>
          mutate(
            `${effectKindDisplayName(node.kind)} ${descriptor.name}`,
            (open) => {
              const live = open.master.chain.nodes.find((entry) => entry.id === node.id);
              if (live !== undefined) live.values[descriptor.key] = value;
            },
            { coalesceKey: `master-${node.id}-${descriptor.key}` },
          ),
      }).el,
  );

  return h(
    'div',
    { class: ['rack-node', node.isBypassed ? 'rack-node--bypassed' : ''] },
    h(
      'div',
      { class: 'rack-head' },
      icon(effectKindSymbolName(node.kind), { size: 17 }),
      h('span', { class: 'rack-name' }, effectKindDisplayName(node.kind)),
      iconButton({
        symbol: 'chevron.up',
        label: 'Move earlier',
        enabled: index > 0,
        onPress: () =>
          editMaster('Reorder Effects', (nodes) => {
            const [moved] = nodes.splice(index, 1);
            if (moved !== undefined) nodes.splice(Math.max(0, index - 1), 0, moved);
          }),
      }).el,
      iconButton({
        symbol: 'chevron.down',
        label: 'Move later',
        enabled: index < total - 1,
        onPress: () =>
          editMaster('Reorder Effects', (nodes) => {
            const [moved] = nodes.splice(index, 1);
            if (moved !== undefined) nodes.splice(Math.min(nodes.length, index + 1), 0, moved);
          }),
      }).el,
      iconButton({
        symbol: node.isBypassed ? 'speaker.slash.fill' : 'checkmark.circle.fill',
        label: node.isBypassed ? 'Enable effect' : 'Bypass effect',
        active: !node.isBypassed,
        onPress: () =>
          editMaster('Bypass Effect', (nodes) => {
            const live = nodes.find((entry) => entry.id === node.id);
            if (live !== undefined) live.isBypassed = !live.isBypassed;
          }),
      }).el,
      iconButton({
        symbol: 'trash.fill',
        label: 'Remove effect',
        onPress: () =>
          editMaster('Remove Effect', (nodes) => {
            const at = nodes.findIndex((entry) => entry.id === node.id);
            if (at >= 0) nodes.splice(at, 1);
          }),
      }).el,
    ),
    h('div', { class: 'rack-params' }, ...controls),
  );
}

export function mixerScreen(): HTMLElement {
  if (project.peek() === null) return requiresProject();

  const loadedPads = computed(() => {
    revision.value;
    const open = project.value;
    if (open === null) return [] as Pad[];
    return open.banks.flatMap((bank) => bank.pads).filter((pad) => !padIsEmpty(pad));
  });

  const strips = h('div', { class: 'mixer-strips scroll-x' });
  // `list()` keeps the element and calls this instead of re-rendering, so anything set only
  // in `padStrip` is frozen at build time. `aria-pressed` and the two `aria-label`s were:
  // a muted pad went on announcing "Mute Kick, not pressed" for ever, leaving the mute state
  // carried by a CSS class alone, and a rename never reached the spoken names.
  list(strips, loadedPads, (pad) => pad.id, padStrip, (element, pad) => {
    const label = element.querySelector('.kicker');
    const name = pad.name === '' ? `Pad ${pad.slot + 1}` : pad.name;
    if (label !== null && label.textContent !== name) label.textContent = name;

    const flags = element.querySelectorAll('.strip-flag');
    const mute = flags[0];
    if (mute !== undefined) {
      mute.classList.toggle('strip-flag--mute', pad.isMuted);
      mute.setAttribute('aria-pressed', pad.isMuted ? 'true' : 'false');
      mute.setAttribute('aria-label', `Mute ${name}`);
    }
    const solo = flags[1];
    if (solo !== undefined) {
      solo.classList.toggle('strip-flag--solo', pad.isSoloed);
      solo.setAttribute('aria-pressed', pad.isSoloed ? 'true' : 'false');
      solo.setAttribute('aria-label', `Solo ${name}`);
    }

    // The position, not just the name. `list()` keeps the row and calls this instead of
    // rebuilding it, and the input's `value` was written once at construction — so any model
    // change that kept the row alive (undo, redo, a project reload, an automation replay)
    // left the fader showing a level the audio no longer had. Skipped while the control has
    // focus, because writing to a range input mid-drag fights the finger holding it.
    const fader = element.querySelector('.fader') as HTMLInputElement | null;
    if (fader !== null) {
      fader.setAttribute('aria-label', `${name} volume`);
      if (document.activeElement !== fader && Number.parseFloat(fader.value) !== pad.volume) {
        fader.value = String(pad.volume);
      }
    }
    const panner = element.querySelector('.pan') as HTMLInputElement | null;
    if (panner !== null) {
      panner.setAttribute('aria-label', `${name} pan`);
      if (document.activeElement !== panner && Number.parseFloat(panner.value) !== pad.pan) {
        panner.value = String(pad.pan);
      }
    }
  });

  const master = stereoMeter({ orientation: 'horizontal', segments: 24, title: 'Master output', class: 'master-meter' });
  const reduction = gainReductionMeter({ maxReductionDB: 18, orientation: 'horizontal', title: 'Limiter gain reduction' });
  const releaseMeters = engine.acquireMeters();
  const stopMeters = effect(() => {
    engine.meterTick.value;
    const level = engine.masterLevel();
    master.setLevel({ leftRMS: level.leftRMS, rightRMS: level.rightRMS, leftPeak: level.leftPeak, rightPeak: level.rightPeak });
    reduction.setGain(Math.pow(10, engine.gainReductionDB() / 20));
  });

  const masterVolume = knob({
    descriptor: { key: 'volume', name: 'Master', min: 0, max: 1, defaultValue: 0.85, unit: '', scale: 'linear', randomisable: false },
    value: computed(() => {
      revision.value;
      return project.value?.master.volume ?? 0.85;
    }),
    onChange: (value) => {
      mutate('Master Volume', (open) => (open.master.volume = value), { coalesceKey: 'master-volume' });
      sequencer.writeAutomation(masterVolumeTarget(), value);
    },
  });

  const headroom = knob({
    descriptor: { key: 'headroom', name: 'Headroom', min: -24, max: 0, defaultValue: -3, unit: 'dB', scale: 'linear', randomisable: false },
    value: computed(() => {
      revision.value;
      return project.value?.master.headroomDB ?? -3;
    }),
    onChange: (value) => mutate('Headroom', (open) => (open.master.headroomDB = value), { coalesceKey: 'headroom' }),
  });

  const ceiling = knob({
    descriptor: { key: 'ceiling', name: 'Ceiling', min: -12, max: 0, defaultValue: -0.5, unit: 'dB', scale: 'linear', randomisable: false },
    value: computed(() => {
      revision.value;
      return project.value?.master.limiterCeilingDB ?? -0.5;
    }),
    onChange: (value) => mutate('Limiter Ceiling', (open) => (open.master.limiterCeilingDB = value), { coalesceKey: 'ceiling' }),
  });

  const screen = h(
    'div',
    { class: 'screen screen--mixer' },
    screenHeader({
      kicker: 'Levels and master',
      title: 'Mixer',
      actions: [
        iconButton({ symbol: 'square.and.arrow.up', label: 'Export', onPress: () => void navigate({ name: 'export', projectID: project.peek()?.id }) }).el,
      ],
    }),
    screenBody(
      section(
        'Channels',
        strips,
        computed(() => loadedPads.value.length).peek() === 0
          ? emptyState({
              symbol: 'square.grid.3x3.fill',
              title: 'No loaded pads',
              message: 'Assign a sound to a pad and its channel strip appears here.',
            })
          : null,
      ),
      section(
        'Master',
        h('div', { class: 'master-row' }, masterVolume.el, headroom.el, ceiling.el),
        master.el,
        h(
          'div',
          { class: 'master-limiter' },
          h('p', { class: 'kicker' }, 'Limiter gain reduction'),
          reduction.el,
        ),
        toggle({
          title: 'Master limiter',
          subtitle: 'Catches the peaks when several pads land together. Leave it on unless you are bouncing stems.',
          on: computed(() => {
            revision.value;
            return project.value?.master.limiterEnabled ?? true;
          }),
          onChange: (on) => mutate('Limiter', (open) => (open.master.limiterEnabled = on)),
        }).el,
      ),
      section(
        'Master effects',
        masterRack(),
        neonButton({
          title: 'Add master effect',
          style: 'secondary',
          onPress: () =>
            presentMenu(
              'Add to the master chain',
              EFFECT_KINDS.map((kind) => ({
                title: effectKindDisplayName(kind),
                symbol: effectKindSymbolName(kind),
                onSelect: () => editMaster('Add Effect', (nodes) => nodes.push(makeEffectNode(kind))),
              })),
            ),
        }).el,
      ),
    ),
  );

  onCleanup(screen, () => {
    stopMeters();
    releaseMeters();
    master.destroy();
    reduction.destroy();
    masterVolume.destroy();
    headroom.destroy();
    ceiling.destroy();
  });
  return screen;
}
